<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="readonly">{{ $t('kiosk.title') }}</h1>
      <h1 v-else-if="form.id">{{ $t('common.change')}}</h1>
      <h1 v-else>{{ $t('kiosk.new_kiosk')}}</h1>
      <div class="details-component__header_right" v-if="readonly && kiosk.id">
        <router-link :to="{ name: 'edit-kiosk', params: { id: kiosk.id } }" class="link">
          {{ $t('common.change') }}&nbsp;<i class="icon icon--arrow"></i>
        </router-link>
        <a class="link link--orange" @click="showConfirmationModal = true">
          {{ $t('common.delete') }}&nbsp;<i class="icon icon--cross"></i>
        </a>
      </div>
    </div>
    <div class="details-component__content">
      <form class="form" v-if="!isLoading">
        <fieldset class="form__fieldset" style="margin-top: 50px">
            <div class="form__field">
              <label for="kiosk_type" class="form__label">Kiosk type</label>
              <input v-if="readonly" type="text" class="form__input" :value="kioskTypeLabel" disabled />
              <el-select v-else name="kiosk_type" v-model="form.kiosk_type" placeholder="">
                <el-option label="Guest parking kiosk" value="guest_parking_kiosk" />
                <el-option label="Franky" value="franky" />
              </el-select>
            </div>
            <div v-if="isKiosksRoute" class="form__field">
              <label class="form__label">{{ $t('common.tenant') }}</label>
              <router-link v-if="readonly && form.tenantId" :to="{ name: 'tenant', params: { id: form.tenantId } }" class="link">{{ form.tenant_name }}</router-link>
              <input v-else-if="readonly" type="text" class="form__input" :value="form.tenant_name" disabled />
              <el-select
                v-else
                :key="tenantOptions.length"
                v-model="selectedTenantId"
                placeholder=""
                @change="onTenantChange">
                <el-option
                  v-for="t in tenantOptions"
                  :label="t.name"
                  :value="t.id"
                  :key="t.id">
                </el-option>
              </el-select>
            </div>
            <div class="form__field" :class="{'form__field_error' : errors.has('product')}">
                <label for="product" class="form__label">{{ $t('common.product') }}</label>
                <router-link v-if="readonly && form.productId" :to="{ name: 'product', params: { id: form.productId } }" class="link">{{ form.product_name }}</router-link>
                <input v-else-if="readonly" type="text" class="form__input" :value="form.product_name" disabled />
                <el-select v-else name="product"
                  v-model="productId"
                  placeholder=""
                  v-validate="'required'">
                  <el-option
                    v-for="product in products.items"
                    :label="product.name"
                    :value="product.id"
                    :key="product.id">
                  </el-option>
                </el-select>
                <tooltip v-show="errors.has('product')" class="form__validation">{{ $t('errors.product_field_required') }}</tooltip>
            </div>
            <div class="form__field">
                <label for="description" class="form__label">{{ $t('kiosk.description')}}</label>
                <input type="text" class="form__input" v-model.trim="form.description" id="description" data-vv-as="description" name="description" :disabled="readonly" />
            </div>
            <div v-if="isSuperAdmin() || isLandlord()" class="form__field">
              <label for="pin" class="form__label">{{ $t('kiosk.pin')}}</label>
              <input type="text" class="form__input" v-model.trim="form.pin" id="pin" data-vv-as="pin" name="pin" v-validate="{ rules: { regex: /^[A-Z0-9]+$/} }" :disabled="readonly" />
              <tooltip v-show="errors.has('pin')" class="form__validation">{{ $t('errors.invalid_pin_field') }}</tooltip>
            </div>
          <!-- Franky-only fields -->
          <template v-if="form.kiosk_type === 'franky'">
            <div v-if="isLandlord()" class="form__field">
              <label for="pos_id" class="form__label">POS terminal ID</label>
              <input type="text" class="form__input" v-model.trim="form.pos_id" id="pos_id" data-vv-as="pos_id" name="pos_id" :disabled="readonly" />
            </div>
            <div v-if="terminals.length > 1" class="form__field" :class="{'form__field_error' : errors.has('terminal')}">
              <label for="terminal" class="form__label">{{ $t('common.terminal') }}</label>
              <input v-if="readonly" type="text" class="form__input" :value="form.terminal_provider" disabled />
              <el-select v-else name="terminal"
                         v-model="form.terminal_provider"
                         placeholder="">
                <el-option
                  v-for="terminal in terminals"
                  :label="terminal"
                  :value="terminal"
                  :key="terminal">
                </el-option>
              </el-select>
            </div>
            <div class="form__field">
              <label for="langbarPos" class="form__label">{{ $t('kiosk.langbarPos') }}</label>
              <input v-if="readonly" type="text" class="form__input" :value="langbarPosLabel" disabled />
              <el-select v-else name="langbarPos"
                         v-model="form.langbarPos"
                         placeholder="">
                <el-option
                  :label="$t('kiosk.top')"
                  value="top">
                </el-option>
                <el-option
                  :label="$t('kiosk.bottom')"
                  value="bottom">
                </el-option>
              </el-select>
            </div>
            <div class="form__field" v-if="availableLanguages.length">
              <label class="form__label">{{ $t('kiosk.languages') }}</label>
              <div v-for="lang in availableLanguages" :key="lang" style="display: inline-block; margin-right: 16px;">
                <label style="display: flex; align-items: center; gap: 6px; cursor: pointer;">
                  <input type="checkbox"
                    :value="lang"
                    v-model="form.languages"
                    :disabled="readonly"
                    style="height: 16px; width: 16px;" />
                  {{ lang.toUpperCase() }}
                </label>
              </div>
            </div>
          </template>

          <!-- Regular-only fields -->
          <template v-if="form.kiosk_type !== 'franky'">
            <div v-if="isLandlord()" class="form__field">
                <label for="hash" class="form__label">{{ $t('kiosk.hash')}}</label>
                <input type="text" class="form__input" v-model.trim="form.kiosk_hash" id="hash" data-vv-as="hash" name="hash" v-validate="'min:20, max:50'" :disabled="readonly" />
                <tooltip v-show="errors.has('hash')" class="form__validation">{{ $t('errors.invalid_hash_field') }}</tooltip>
            </div>
            <div v-if="isLandlord()" class="form__field">
                <label for="hash_enabled" class="form__label">{{ $t('kiosk.hashEnabled')}}</label>
                <input style="height: 20px" type="checkbox" v-model.trim="form.hash_enabled" id="hash_enabled" data-vv-as="hash_enabled" name="hash_enabled" :disabled="readonly">
            </div>
            <div v-if="isLandlord()" class="form__field">
                <label for="is_show_ocupancy" class="form__label">Show ocupancy</label>
                <input style="height: 20px" type="checkbox" v-model.trim="form.is_show_ocupancy" id="is_show_ocupancy" data-vv-as="is_show_ocupancy" name="is_show_ocupancy" :disabled="readonly">
            </div>
            <div v-if="isLandlord()" class="form__field">
                <label for="enable_session_closing" class="form__label">{{ $t('kiosk.enableSessionClosing')}}</label>
                <input style="height: 20px" type="checkbox" v-model.trim="form.enable_session_closing" id="enable_session_closing" data-vv-as="enable_session_closing" name="enable_session_closing" :disabled="readonly">
            </div>
            <div v-if="isLandlord()" class="form__field">
                <label for="is_friida_kiosk" class="form__label">Friida kiosk</label>
                <input style="height: 20px" type="checkbox" v-model="form.is_friida_kiosk" id="is_friida_kiosk" data-vv-as="is_friida_kiosk" name="is_friida_kiosk" :disabled="readonly">
            </div>
            <div v-if="isLandlord() && form.is_friida_kiosk" class="form__field">
                <label for="eps_zone_id" class="form__label">EPS Zone ID</label>
                <input type="number" class="form__input" v-model.number="form.eps_zone_id" id="eps_zone_id" data-vv-as="eps_zone_id" name="eps_zone_id" :disabled="readonly" />
            </div>
            <div v-if="isLandlord() && form.is_friida_kiosk" class="form__field">
                <label for="eps_service_price_id" class="form__label">EPS Service Price ID</label>
                <input type="number" class="form__input" v-model.number="form.eps_service_price_id" id="eps_service_price_id" data-vv-as="eps_service_price_id" name="eps_service_price_id" :disabled="readonly" />
            </div>
            <div class="form__field">
                <label for="screensaver_enabled" class="form__label">{{ $t('kiosk.enableScreensaver')}}</label>
                <input style="height: 20px" type="checkbox" v-model.trim="form.screensaver_enabled" id="screensaver_enabled" data-vv-as="screensaver_enabled" name="screensaver_enabled" :disabled="readonly">
            </div>
            <div class="form__field">
                <label for="screensaver_show_after" class="form__label">{{ $t('kiosk.screensaverShowAfter')}}</label>
                <input type="number" class="form__input" v-model.trim="form.screensaver_show_after" id="screensaver_show_after" data-vv-as="screensaver_show_after" name="screensaver_show_after" :disabled="readonly" />
            </div>
            <div class="form__field">
                <label for="qr_code_enabled" class="form__label">{{ $t('kiosk.enableQrCode')}}</label>
                <input style="height: 20px" type="checkbox" v-model.trim="form.qr_code_enabled" id="qr_code_enabled" data-vv-as="qr_code_enabled" name="qr_code_enabled" :disabled="readonly">
            </div>
            <div class="form__field">
                <label for="logo" class="form__label">{{ $t('kiosk.logo')}}</label>
                <input type="text" class="form__input" v-model.trim="form.logo" id="logo" data-vv-as="logo" name="logo" :disabled="readonly" />
            </div>
            <div class="form__field form__field_textarea" :class="{'form__field_error' : errors.has('logoMeta')}">
                <label for="logoMeta" class="form__label">{{ $t('kiosk.logo_meta')}}</label>
                <textarea type="textarea" class="form__input" v-model.trim="form.logoMeta" v-validate data-vv-rules="validJson" id="logoMeta" name="logoMeta" rows="5" :disabled="readonly"></textarea>
                <tooltip v-show="errors.has('logoMeta')" class="form__validation">{{ $t('errors.invalid_json_format') }}</tooltip>
            </div>
            <div class="form__field form__field_textarea" :class="{'form__field_error' : errors.has('colors')}">
                <label for="colors" class="form__label">{{ $t('kiosk.colors')}}</label>
                <textarea type="textarea" class="form__input" v-model.trim="form.colors" v-validate data-vv-rules="validJson" id="colors" name="colors" rows="5" :disabled="readonly"></textarea>
                <tooltip v-show="errors.has('colors')" class="form__validation">{{ $t('errors.invalid_json_format') }}</tooltip>
            </div>
            <div class="form__field" v-if="!readonly">
                <label for="autloDiscount" class="form__label">{{ $t('kiosk.autloDiscount') }}</label>
                <input type="file" class="form__input" id="autloDiscountUpload" data-vv-as="autloDiscountUpload" name="autloDiscountUpload" accept="image/*" @change="processQrFile($event)"/>
                <button type="button" v-on:click="removeDiscountQr">{{$t('kiosk.removeDiscount')}}</button>
            </div>
            <div class="form__field">
              <label class="form__label">{{ $t('kiosk.currentDiscountQr') }}</label>
              <span>{{form.autloDiscount ? '' : $t('kiosk.noDiscount')}}</span>
              <img style="max-height: 200px" v-bind:src="form.autloDiscount" />
            </div>
          </template>
        </fieldset>

        <KioskTicketTypes v-if="!isLoading && kioskId && form.kiosk_type === 'franky'" :kioskId="kioskId" :canEditTypes="isSuperAdmin ? true : kiosk.can_edit_kiosk_permits"/>

        <div class="form__buttons buttonset" v-if="!readonly">
          <router-link :to="backRoute" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="onSubmit()">
            {{ $t('common.save') }}
          </button>
        </div>
      </form>
    </div>

    <KioskPermits v-if="!isLoading && kioskId && form.kiosk_type === 'franky'" :kioskId="kioskId"/>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="showConfirmationModal = false" @confirmed="removeKiosk">
      <h1 slot="header">{{ $t('common.removing') }}: {{ kiosk.description }}</h1>
      <p slot="body">{{ $t('kiosk.confirm_remove') }}</p>
    </ConfirmationModal>
    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ warningHeader }}</h1>
      <p slot="body">{{ warningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import 'styles/blocks/details.scss';
import Autocomplete from 'common/Autocomplete';
import Tooltip from 'common/Tooltip';
import WarningModal from 'common/WarningModal';
import ConfirmationModal from 'common/ConfirmationModal';
import * as mTypes from 'store/mutation-types';
import KioskTicketTypes from "./ticket-types/index";
import KioskPermits from "./kiosk-permits/index"

export default {
  components: {
    Autocomplete,
    Tooltip,
    WarningModal,
    ConfirmationModal,
    KioskTicketTypes,
    KioskPermits
  },
  props: {
    readonly: { type: Boolean, default: false },
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    isKiosksRoute() {
      return this.$route.matched.some(r => r.name === 'kiosks');
    },
    backRoute() {
      if (this.isKiosksRoute) {
        if (this.$route.name === 'edit-kiosk') {
          return { name: 'kiosk', params: { id: this.$route.params.id } };
        }
        return { name: 'kiosks' };
      }
      return { name: 'tenant', params: { id: this.tenant.id } };
    },
    tenantOptions() {
      // the tenants list is fetched with a limit, so the kiosk's own tenant may
      // not be in it; inject it so the select can display its name
      const items = this.tenants.items || [];
      if (this.selectedTenantId && !items.some(t => t.id === this.selectedTenantId)) {
        const name = this.form.tenant_name || (this.kiosk && this.kiosk.tenant_name);
        if (name) {
          return [{ id: this.selectedTenantId, name }, ...items];
        }
      }
      return items;
    },
    kioskTypeLabel() {
      const labels = { guest_parking_kiosk: 'Guest parking kiosk', regular: 'Guest parking kiosk', franky: 'Franky' };
      return labels[this.form.kiosk_type] || this.form.kiosk_type;
    },
    langbarPosLabel() {
      const labels = { top: this.$t('kiosk.top'), bottom: this.$t('kiosk.bottom') };
      return labels[this.form.langbarPos] || this.form.langbarPos;
    },
    ...mapGetters({
      products: 'landlord/products/products',
      tenant: 'tenants/tenant/tenant',
      tenants: 'landlord/tenants/tenants',
      user: 'user/user/user',
      kiosk: 'kiosks/kiosk/kiosk',
      terminals: 'kiosks/terminals/terminals',
      kioskError: 'kiosks/kiosk/error',
      availableLanguages: 'kiosks/kiosk/availableLanguages',
      defaultLanguages: 'kiosks/kiosk/defaultLanguages',
    }),
  },
  data() {
    return {
      form: { kiosk_type: 'guest_parking_kiosk' },
      productId: null,
      selectedTenantId: null,
      warningHeader: null,
      warningBody: null,
      showWarningModal: false,
      kioskId: null,
      isLoading: false,
      showConfirmationModal: false
    };
  },
  watch: {
    '$route.params.id'(id) {
      if (id) {
        this.fetchKiosk();
      } else {
        this.form = { kiosk_type: 'guest_parking_kiosk' };
        this.productId = null;
        this.kioskId = null;
        this.selectedTenantId = null;
      }
    },
    readonly(isReadonly) {
      if (!isReadonly && this.$route.params.id) {
        this.fetchKiosk();
      }
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.fetchKiosk();
    }
    this.fetchTerminals();
  },
  created() {
    if (!this.readonly && this.isNew) {
      if (this.isKiosksRoute) {
        this.fetchAllTenants();
      } else {
        this.fetchProducts();
      }
    }
    this.$validator.remove('validJson');
    this.$validator.extend('validJson', {
      getMessage: () => 'Please insert valid json.',
      validate: (value) => {
        try {
          JSON.parse(value);
        } catch (e) {
          return false;
        }
        return true;
      },
    });
  },
  methods: {
    fetchKiosk() {
      if (!this.readonly) {
        this.isLoading = true;
        this.form = { kiosk_type: 'guest_parking_kiosk' };
        this.productId = null;
        this.kioskId = null;
        this.selectedTenantId = null;
      }
      return this.$store.dispatch('kiosks/kiosk/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
        this.form = { ...this.kiosk };
        this.form.kiosk_type = this.kiosk.kiosk_type || 'guest_parking_kiosk';
        this.form.autopay_enabled = this.kiosk.autopay_enabled;
        this.kioskId = this.form.id;
        this.selectedTenantId = this.kiosk.tenantId || (this.tenant && this.tenant.id);
        if (!this.readonly) {
          // the store may hold a partial (paginated/filtered) tenants list from the
          // tenants sidebar; refetch unless the kiosk's tenant is actually in it
          const tenantsReady = (this.isKiosksRoute && !this.hasTenantOption(this.selectedTenantId))
            ? this.$store.dispatch('landlord/tenants/fetchByRole', { user: this.user, limit: 1000 })
            : Promise.resolve();
          return Promise.all([tenantsReady, this.fetchProducts(this.selectedTenantId)]).then(() => {
            this.productId = this.kiosk.productId;
          });
        }
        this.productId = this.kiosk.productId;
      }).finally(() => {
        this.isLoading = false;
      });
    },
    fetchTerminals() {
      this.$store.dispatch('kiosks/terminals/fetch').then(() => {});
    },
    hasTenantOption(tenantId) {
      return !!tenantId && (this.tenants.items || []).some(t => t.id === tenantId);
    },
    fetchAllTenants() {
      // always refetch: the store may hold a partial list from the tenants sidebar
      this.$store.dispatch('landlord/tenants/fetchByRole', { user: this.user, limit: 1000 });
    },
    fetchAllProducts() {
      this.$store.dispatch('landlord/products/fetch', { user: this.user, type: 'guestParking', limit: 1000 });
    },
    onTenantChange(tenantId) {
      this.selectedTenantId = tenantId;
      this.productId = null;
      this.fetchProducts(tenantId);
    },
    fetchProducts(tenantId) {
      const id = tenantId || (this.tenant && this.tenant.id);
      if (id) {
        return this.$store.dispatch('landlord/products/fetchTenantProducts', { id, user: this.user, type: 'guestParking', limit: 1000 });
      }
      return Promise.resolve();
    },
    processQrFile(event) {
      const reader = new FileReader();
      reader.addEventListener('load', this.onQrReaderFileLoaded, false);
      if (/image\/.*/.test(event.target.files[0].type)) {
        reader.readAsDataURL(event.target.files[0]);
      }
    },
    onQrReaderFileLoaded(event) {
      this.form.autloDiscount = event.target.result;
    },
    removeDiscountQr() {
      this.form.autloDiscount = '';
    },
    onSubmit() {
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.form.productId = this.productId;
          this.form.tenantId = this.selectedTenantId || (this.tenant && this.tenant.id);
          this.$store.dispatch('kiosks/kiosk/add', { kiosk: this.form, user: this.user }).then(() => {
            if (this.kioskError) {
              this.warningHeader = this.$t('common.warning');
              this.warningBody = this.$t(`errors.${this.kioskError}`);
              this.showWarningModal = true;
            } else {
              this.form = { kiosk_type: 'guest_parking_kiosk' };
              if (this.isKiosksRoute) {
                if (this.$route.name === 'edit-kiosk') {
                  this.$router.push({ name: 'kiosk', params: { id: this.kioskId } });
                } else {
                  this.$router.push({ name: 'kiosks' });
                }
              } else {
                this.$router.push({ name: 'tenant', params: { id: this.tenant.id } });
              }
            }
          }).catch(() => {
            this.$store.commit(`kiosks/kiosk/${mTypes.DATA_ERROR}`, true);
          });
          this.$validator.clean();
          this.$store.commit(`kiosks/kiosk/${mTypes.DATA_ERROR}`, null);
        }).catch(() => {});
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`kiosks/kiosk/${mTypes.DATA_ERROR}`, null);
    },
    removeKiosk() {
      this.showConfirmationModal = false;
      this.$store.dispatch('kiosks/kiosk/delete', { id: this.kiosk.id, user: this.user }).then(() => {
        this.$store.dispatch('kiosks/kiosks/fetch', { user: this.user });
        this.$router.push({ name: 'kiosks' });
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/tenants/AddKioskView.vue